import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Users, Briefcase } from 'lucide-react';

const familyPlusOutcomes = [
"Advanced care coordination tools",
"Expanded activity and wellness visibility",
"Clearer accountability across family members",
"Stronger alignment without micromanaging"];


const thriveCareOutcomes = [
"Care visit documentation",
"Voice notes and client summaries",
"Task tracking and accountability",
"Clear communication between caregivers and family",
"Professional-grade reporting"];


const ExpansionSection = () => {
  return (
    <section className="mb-16 py-12 px-4 rounded-2xl bg-muted/30 border border-border/40">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-8">
          <h2 className="text-2xl lg:text-3xl font-bold text-foreground mb-3">
            When More Care Is Needed, SeniorThrive Expands With You
          </h2>
          <p className="text-muted-foreground text-base mb-4">
            Start simple. Add structure as responsibilities grow.
          </p>
          <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
            Aging at home changes over time. Sometimes a family member steps into a larger role. Sometimes professional caregivers join the picture. SeniorThrive adapts without forcing you to change systems.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 items-stretch">
          {/* ThriveFamily+ */}
          <Card className="border border-border/60 bg-background flex flex-col">
            <CardContent className="pt-8 pb-8 flex flex-col flex-1">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Users className="w-5 h-5 text-primary" />
                </div>
                <Badge variant="outline" className="text-xs">Add-On Module</Badge>
              </div>
              <h3 className="text-xl font-bold text-foreground mb-2">ThriveFamily+</h3>
              <p className="text-sm text-muted-foreground mb-1 font-medium">
                For Families Taking the Lead
              </p>
              <div className="flex items-baseline gap-1.5 mb-1">
                <span className="text-2xl font-semibold text-foreground">$10</span>
                <span className="text-sm text-muted-foreground">/month</span>
              </div>
              <p className="text-xs text-muted-foreground mb-6">As an add-on to an active ThriveFamily subscription.</p>
              <p className="text-sm text-muted-foreground mb-6">When a family member becomes the primary coordinator, ThriveFamily+ adds deeper tools for managing care.</p>

              <ul className="space-y-3 mb-8">
                {familyPlusOutcomes.map((outcome, i) =>
                <li key={i} className="flex items-start gap-3 text-sm text-foreground/80">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                    {outcome}
                  </li>
                )}
              </ul>

              <div className="flex-1" />
              <Button variant="outline" className="w-full" asChild>
                <a href="/family-caregiver-tools">
                  Learn About ThriveFamily+
                  <ArrowRightIcon className="w-4 h-4 ml-2" />
                </a>
              </Button>
            </CardContent>
          </Card>

          {/* ThriveCare */}
          <Card className="border border-border/60 bg-background flex flex-col">
            <CardContent className="pt-8 pb-8 flex flex-col flex-1">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Briefcase className="w-5 h-5 text-primary" />
                </div>
                <Badge variant="outline" className="text-xs">Add-On Module</Badge>
              </div>
              <h3 className="text-xl font-bold text-foreground mb-2">ThriveCare</h3>
              <p className="text-sm text-muted-foreground mb-1 font-medium">
                For Professional Care Teams
              </p>
              <div className="flex items-baseline gap-1.5 mb-1">
                <span className="text-2xl font-semibold text-foreground">$15</span>
                <span className="text-sm text-muted-foreground">/month per caregiver seat</span>
              </div>
              <p className="text-xs text-muted-foreground mb-6">As an add-on to an active ThriveFamily subscription.</p>
              <p className="text-sm text-muted-foreground mb-6">
                When professional caregivers join the household, ThriveCare adds structured documentation and reporting tools.
              </p>

              <ul className="space-y-3 mb-8">
                {thriveCareOutcomes.map((outcome, i) =>
                <li key={i} className="flex items-start gap-3 text-sm text-foreground/80">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                    {outcome}
                  </li>
                )}
              </ul>

              <div className="flex-1" />
              <Button variant="outline" className="w-full" asChild>
                <a href="/features">
                  Learn About ThriveCare
                  <ArrowRightIcon className="w-4 h-4 ml-2" />
                </a>
              </Button>
            </CardContent>
          </Card>
        </div>

        <p className="text-center text-xs text-muted-foreground mt-6">
          Add-ons require an active ThriveFamily subscription.
        </p>
      </div>
    </section>);

};

export default ExpansionSection;